/* eslint-disable react/jsx-props-no-spreading */
import { ReactElement, Suspense } from 'react'
import styled from 'styled-components'
import { isIE, isSafari } from 'helpers'
import { Route, RouteComponentProps, RouteProps } from 'react-router-dom'
import { colors } from 'const/colors'
import ErrorBoundary from 'ui/ErrorBoundaries/ErrorBoundary'
import { SpinnerLoader } from 'syf-component-library/ui/atoms'
import { Grid } from 'syf-component-library/ui/patterns'

/*
 *     Support: Safari Sticky Footer
 * Description: Safari has a arguably buggy behavior when using `vh` units.
 *         Fix: Use `-webkit-fill-available` meant for mobile devices.
 *    Resource: https://allthingssmitty.com/2020/05/11/css-fix-for-100vh-in-mobile-webkit/
 */
export const AppContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin: 0 auto;
  height: ${isSafari() ? '-webkit-fill-available' : '100vh'};
  max-width: 1024px;
`

export const Main = styled(props => {
  return isIE() ? (
    <div {...props} role="main">
      {props.children}
    </div>
  ) : (
    <main {...props}>{props.children}</main>
  )
})`
  flex: 1 0 auto;
  background-color: ${colors.white};
`

export const PublicRoute = ({
  component: Component,
  ...props
}: RouteProps): ReactElement => {
  return (
    <Route
      {...props}
      render={(routeProps: RouteComponentProps): ReactElement => (
        <ErrorBoundary>
          <Component {...routeProps} />
        </ErrorBoundary>
      )}
    />
  )
}

const FullHeightGrid = styled(Grid)`
  min-height: 100%;
`
export const PageLoader = (): ReactElement => (
  <FullHeightGrid alignContent="center" justifyContent="center">
    <SpinnerLoader size="3x" />
  </FullHeightGrid>
)

/**
 * HOC which takes a component which renders a Promise (e.g. result of
 * React.lazy()) and wraps it in Suspense using the PageLoader as the fallback.
 *
 * @param Component Component to wrap in suspense.
 */
export const withPageLoader = (Component: React.ComponentType) =>
  function renderWithPageLoader() {
    return (
      <Suspense fallback={<PageLoader />}>
        <Component />
      </Suspense>
    )
  }
